import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { v: "customer", label: "Customer" },
  { v: "delivery", label: "Delivery Partner" },
  { v: "retailer", label: "Retailer / Store" },
  { v: "influencer", label: "Influencer" },
];

const CATEGORIES = [
  { v: "grocery", label: "Grocery & Staples" },
  { v: "fruits-vegetables", label: "Fruits & Vegetables" },
  { v: "dairy", label: "Dairy & Eggs" },
  { v: "bakery", label: "Bakery" },
  { v: "beverages", label: "Beverages" },
  { v: "snacks", label: "Snacks" },
  { v: "personal-care", label: "Personal Care" },
  { v: "household", label: "Household" },
];

export default function Register() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { register, retailerSignup } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", role: "customer",
    store_name: "", category: "grocery", address: "", city: "",
  });
  useEffect(() => {
    const r = params.get("role");
    if (["customer", "delivery", "retailer", "influencer"].includes(r)) setForm((f) => ({ ...f, role: r }));
  }, [params]);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isRetailer = form.role === "retailer";

  const submit = async (e) => {
    e.preventDefault();
    if (isRetailer && !form.store_name.trim()) return toast.error("Enter your store name");
    setLoading(true);
    try {
      if (isRetailer) {
        await retailerSignup({
          name: form.name, email: form.email, password: form.password, phone: form.phone,
          store_name: form.store_name, category: form.category, address: form.address, city: form.city,
        });
        toast.success("Store account created! Verify your account to go live.");
        nav("/retailer");
      } else {
        const user = await register({
          name: form.name, email: form.email, password: form.password, phone: form.phone, role: form.role,
        });
        toast.success(user.role === "influencer" ? "Affiliate account created! Awaiting admin approval." : "Account created!");
        nav({ delivery: "/delivery", influencer: "/affiliate?verify=1" }[user.role] || "/");
      }
    } catch (err) { toast.error(apiError(err)); }
    setLoading(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-deep grain p-4">
      <div className="relative z-10 w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl">
        <Link to="/" className="mb-6 flex items-center">
          <img src="/logo.png" alt="Grofkart" className="h-9 w-auto" />
        </Link>
        <h1 className="font-head text-2xl font-extrabold text-slate-900">Create your account</h1>
        <p className="mb-6 text-sm text-slate-500">Join Grofkart in seconds</p>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {ROLES.map((r) => (
              <button type="button" key={r.v} onClick={() => setForm({ ...form, role: r.v })}
                data-testid={`register-role-${r.v}`}
                className={`rounded-xl border px-2 py-2 text-xs font-semibold ${form.role === r.v ? "border-brand bg-brand-soft text-brand" : "border-slate-200 text-slate-600"}`}>
                {r.label}
              </button>
            ))}
          </div>

          <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="register-name"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand" />
          <input type="email" required value={form.email} onChange={set("email")} placeholder="Email" data-testid="register-email"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand" />
          <input value={form.phone} onChange={set("phone")} placeholder="Phone number" data-testid="register-phone"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand" />
          <input type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="Password (min 6 chars)" data-testid="register-password"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand" />

          {form.role === "influencer" && (
            <div className="rounded-xl border border-pink-200 bg-pink-50/60 p-3">
              <p className="text-xs font-bold uppercase text-pink-600">Influencer / Affiliate</p>
              <p className="mt-1 text-[11px] text-slate-600">Get a unique referral link, earn category-wise commission on every order you refer, and withdraw earnings to your UPI/bank. Your account is activated after a quick admin review.</p>
            </div>
          )}

          {isRetailer && (
            <div className="space-y-3 rounded-xl border border-brand/30 bg-brand-soft/40 p-3">
              <p className="text-xs font-bold uppercase text-brand">Store details</p>
              <input required value={form.store_name} onChange={set("store_name")} placeholder="Store name" data-testid="register-store-name"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
              <select value={form.category} onChange={set("category")} data-testid="register-store-category"
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand">
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select>
              <div className="flex gap-2">
                <input value={form.address} onChange={set("address")} placeholder="Shop address" data-testid="register-store-address"
                  className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
                <input value={form.city} onChange={set("city")} placeholder="City" data-testid="register-store-city"
                  className="w-28 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
              </div>
              <p className="text-[11px] text-slate-500">A one-time <b>₹11 verification fee</b> + a <b>₹2 mandate authentication</b> are charged after signup to activate your <b>3-month free trial</b>.</p>
            </div>
          )}

          <button type="submit" disabled={loading} data-testid="register-submit"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-3 font-bold text-white hover:bg-brand-deep disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} {isRetailer ? "Create store account" : "Create account"}
          </button>
        </form>
        <p className="mt-4 text-center text-sm text-slate-500">
          Already have an account? <Link to="/login" className="font-semibold text-brand">Login</Link>
        </p>
      </div>
    </div>
  );
}
